import {
  Cartesian3,
  Color,
  ColorGeometryInstanceAttribute,
  GeometryInstance,
  GroundPolylineGeometry,
  GroundPolylinePrimitive,
  PolylineColorAppearance,
  type Scene,
} from 'cesium';
import type { RailFeature } from './types';

/**
 * Far-LOD rail layer: every centreline draped on terrain as a coloured ground
 * polyline. Always loaded (cheap, gives instant network context and ensures the
 * near/mid LOD never leaves a visual hole).
 *
 * Colour encodes attributes: electrified = amber, non-operational = grey,
 * otherwise blue.
 */
export function createRailFarLayer(
  scene: Scene,
  features: RailFeature[],
): GroundPolylinePrimitive {
  const instances = features
    .filter((f) => f.line.length >= 2)
    .map(
      (f) =>
        new GeometryInstance({
          geometry: new GroundPolylineGeometry({
            positions: Cartesian3.fromDegreesArray(f.line.flat()),
            width: 2.5,
          }),
          attributes: {
            color: ColorGeometryInstanceAttribute.fromColor(colorFor(f)),
          },
          id: f.id,
        }),
    );

  const primitive = new GroundPolylinePrimitive({
    geometryInstances: instances,
    appearance: new PolylineColorAppearance(),
  });
  scene.groundPrimitives.add(primitive);
  return primitive;
}

function colorFor(f: RailFeature): Color {
  if (f.attributes.operationalStatus && !/operational/i.test(f.attributes.operationalStatus)) {
    return Color.GRAY.withAlpha(0.8);
  }
  if (f.attributes.electrified) return Color.ORANGE;
  return Color.DEEPSKYBLUE;
}
